import clsx from 'clsx';
import { CSSProperties, FunctionComponent, memo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';

import attachmentIcon from '../../../assets/images/icons/attachment-blue.svg';
import ReturnArrowLeftIcon from '../../../assets/images/icons/return-arrow-left.svg';
import TextTruncate from '../../app/components/TextTruncate';
import { useAppDispatch } from '../../../store';
import { CommonAvatar } from '../../../types';
import { setIsComposing } from '../api/inboxSlice';

import MessageAvatarList from './MessageAvatarList';
import { useTranslation } from 'react-i18next';

const MessagesListItem: FunctionComponent<{
  id: string;
  threadId?: string;
  avatars: Array<CommonAvatar>;
  from: Array<string>;
  content: string;
  subject: string;
  timestamp: string;
  isRead: boolean;
  hasAttachments: boolean;
  hasReply?: boolean;
  active?: boolean;
  style?: CSSProperties;
}> = ({
  threadId,
  avatars,
  from,
  content,
  subject,
  timestamp,
  isRead,
  active,
  style,
  hasAttachments,
  hasReply,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onItemClick = () => {
    dispatch(setIsComposing(false));
  };

  const avatarSize = '40px';

  const formatTimestamp = (timestamp: string) => {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  };

  return (
    <Link
      to={`/app/messages/inbox/${threadId}`}
      className={clsx(
        'message__preview',
        { 'message__preview--unread': !isRead },
        { 'message__preview--active': active },
      )}
      style={style}
      onClick={onItemClick}
    >
      <div className='d-flex flex-row align-items-center'>
        <MessageAvatarList
          nameList={from}
          avatars={avatars}
          width={avatarSize}
          height={avatarSize}
          style={{ fontSize: '18px', lineHeight: '22px' }}
        />
        <div
          className={clsx([
            'text-truncate',
            avatars.length === 1 ? 'message__preview-container' : '',
          ])}
        >
          <p
            className={clsx('message__thread__header-full-name', {
              'message__thread__header-full-name--alt': avatars.length === 1,
            })}
            title={from.join(', ')}
          >
            {from[0]} {from.length > 1 && '+' + (from.length - 1)}
          </p>
          <div className='message__preview__subject w-100 text-truncate'>
            <span>{t(subject)}</span>
          </div>
        </div>
      </div>
      <div className='flex-row'>
        <div className='message__preview__timestamp'>
          <div className='d-flex justify-content-end'>
            {hasReply && <img src={ReturnArrowLeftIcon} alt='attachment'></img>}
            {hasAttachments && <img src={attachmentIcon} alt='attachment'></img>}
          </div>
          <div className='mt-3'>{formatTimestamp(timestamp)}</div>
        </div>
      </div>

      <div className='d-flex flex-row'>
        <div className='message__preview__content pt-2'>
          <p className='message__preview__paragraph'>
            <TextTruncate text={content as string} limit={105} />
          </p>
        </div>
      </div>
    </Link>
  );
};

export default memo(MessagesListItem);
